/**
 * Canvas Shape Annotations Component
 *
 * Separate drawing system for shape annotations (Line, Circle, Rectangle)
 * Similar to image annotations but for 3D canvas
 * Does NOT interfere with existing Drawing component (Measure, Wiring, Driveway)
 */

import React, { useState, useContext, useEffect, useRef, useCallback, useMemo } from 'react';
import { Vector3 } from 'three';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { Html, Line as DreiLine } from '@react-three/drei';
import { DesignContext } from '../contexts/designContext.js';
import { intersectRaycastableObjects } from '../algorithms/utils.js';
import { SHAPE_X_MARKER_IMAGE_DATA_URL, SHAPE_X_MARKER_IMG_PX } from '../constants/shapeCrossMarkerDataUrl.js';

const ShapeStroke = ({ points, color }) => (
  <DreiLine
    points={points}
    color={color}
    lineWidth={2}
    transparent
    opacity={1}
    depthTest={false}
    renderOrder={2}
  />
);

const raycaster = new THREE.Raycaster();
const mouse = new THREE.Vector2();

const CanvasShapeAnnotations = ({ canvas, controls, enabled }) => {
  const {
    accordion,
    viewState,
    mode,
    annotations: allAnnotations,
    setAnnotations: setAllAnnotations,
    showTextAnnotationPopup,
    triggerTextAnnotationPopup, // 🆕 Use trigger method if available
    currentShapeType, // 🆕 Current shape type (line, circle, rectangle)
    shapePopupHideTaskCheckbox // 🆕 Checklist shape toolbar: hide Task on placement popup
  } = useContext(DesignContext);

  const { camera, scene, size: canvasSize } = useThree();
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [currentPoint, setCurrentPoint] = useState(null);
  const [shapeAnnotations, setShapeAnnotations] = useState([]);
  const [pendingAnnotation, setPendingAnnotation] = useState(null); // 🆕 Temporary annotation waiting for popup save/cancel
  const pendingAnnotationIdRef = useRef(null); // 🆕 Ref to track pending annotation ID for cancel callback

  // Helper function to return the end point directly
  // The popup's top-left corner (0,0) will be positioned at this point
  // This allows users to visually connect which popup belongs to which annotation
  const calculatePopupPosition = useCallback((endPoint) => {
    // Return the end point directly - popup's top-left will be at this position
    return endPoint.clone();
  }, []);

  // Get 3D position from mouse event (same logic as Annotations.js)
  const get3dPosition = useCallback(
    (e) => {
      if (!canvasSize || !canvasSize.width || !canvasSize.height) {
        return [null, 1.0];
      }

      mouse.set((e.offsetX / canvasSize.width) * 2 - 1, -(e.offsetY / canvasSize.height) * 2 + 1, 0.5);
      raycaster.setFromCamera(mouse, camera);
      // Recurse into groups (roof/ground) and skip sky — non-recursive hits the
      // sky sphere (~Y±600), which looks fine in ortho 2D but shifts pins in 3D.
      const intersects = intersectRaycastableObjects(raycaster, scene.children, true);
      const isSkippable = (obj) =>
        !obj || !obj.visible || obj.name === 'annotationPointer' || obj.name === 'sky' || obj.name === 'wallBorder';

      let fallback = null;
      for (let i = 0; i < intersects.length; i++) {
        const intersect = intersects[i];
        if (isSkippable(intersect.object)) continue;
        let ratio = 1.0;
        if (camera.type === 'PerspectiveCamera')
          ratio = Math.max(0.1, Math.min(1.0, camera.position.length() / 100)).toFixed(2);
        else ratio = Math.max(0.2, Math.min(1.0, 4.0 / camera.zoom)).toFixed(2);
        if (intersect.object.name === 'roofPlane' || intersect.object.name === 'groundPlane') {
          return [intersect.point, ratio];
        }
        if (!fallback) fallback = [intersect.point, ratio];
      }
      return fallback || [null, 1.0];
    },
    [canvasSize, camera, scene]
  );

  // Handle mouse down
  const handleMouseDown = useCallback(
    (e) => {
      // Only activate when in shape-draw mode (separate from regular draw mode)
      if (!enabled || viewState !== 'annotations' || mode !== 'shape-draw') {
        return;
      }
      if (e.button !== 0) return; // Only left click

      const shapeTypeEarly = currentShapeType || 'line';

      // Cross (X): single click — billboard SVG marker + same text/image modal as drag-to-size X
      if (shapeTypeEarly === 'x') {
        const [point, ratio] = get3dPosition(e);
        if (!point) return;

        e.preventDefault();
        e.stopPropagation();

        const r = Math.max(1.5 * Number(ratio), 0.55);
        const newAnnotation = {
          id: `shape-annotation-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: 'ShapeX',
          shape: 'x',
          position: {
            x: point.x,
            y: point.y,
            z: point.z
          },
          radius: r,
          markerImage: SHAPE_X_MARKER_IMAGE_DATA_URL,
          color: '#ff4d4f',
          createdAt: new Date().toISOString()
        };

        setPendingAnnotation(newAnnotation);
        pendingAnnotationIdRef.current = newAnnotation.id;

        if (triggerTextAnnotationPopup) {
          const popupPosition = calculatePopupPosition(point.clone());
          const cancelCallback = () => {
            if (pendingAnnotationIdRef.current === newAnnotation.id) {
              setPendingAnnotation(null);
              pendingAnnotationIdRef.current = null;
            }
          };

          triggerTextAnnotationPopup({
            position: {
              x: popupPosition.x,
              y: popupPosition.y,
              z: popupPosition.z
            },
            annotationData: {
              ...newAnnotation,
              type: newAnnotation.type
            },
            onCancel: cancelCallback,
            hideTaskCheckbox: !!shapePopupHideTaskCheckbox
          });
        }

        // Stay in shape-draw so user can place another X until toolbar deselect / another tool
        return;
      }

      const [point, ratio] = get3dPosition(e);
      if (!point) return;

      e.preventDefault();
      e.stopPropagation();

      if (!isDrawing) {
        // First click - set start point (center for circle, start for line)
        setIsDrawing(true);
        setStartPoint(point.clone());
        setCurrentPoint(point.clone()); // Initialize currentPoint to startPoint
      } else if (startPoint) {
        // Second click - finish the shape
        const distance = startPoint.distanceTo(point);

        // Minimum distance to create annotation (0.1 units)
        if (distance >= 0.1) {
          const shapeType = currentShapeType || 'line'; // Default to line if not set
          let newAnnotation;

          if (shapeType === 'circle') {
            // Circle: startPoint is center, point is edge point
            const radius = distance;
            newAnnotation = {
              id: `shape-annotation-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              type: 'ShapeCircle',
              shape: 'circle',
              position: {
                x: startPoint.x,
                y: startPoint.y,
                z: startPoint.z
              },
              radius: radius,
              color: '#ff4d4f', // Default red
              createdAt: new Date().toISOString()
            };
          } else if (shapeType === 'rectangle') {
            // Rectangle: startPoint is corner 1, point is corner 2 (opposite corner)
            // Calculate center, width, and height
            const center = new Vector3(
              (startPoint.x + point.x) / 2,
              (startPoint.y + point.y) / 2,
              (startPoint.z + point.z) / 2
            );
            // Calculate width and height in 3D space
            // For simplicity, we'll use the distance in XZ plane for width and Y for height
            const width = Math.abs(point.x - startPoint.x);
            const height = Math.abs(point.z - startPoint.z);
            // Use the larger dimension to ensure minimum size
            const minSize = 0.1;
            const finalWidth = Math.max(width, minSize);
            const finalHeight = Math.max(height, minSize);

            newAnnotation = {
              id: `shape-annotation-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              type: 'ShapeRectangle',
              shape: 'rectangle',
              position: {
                x: center.x,
                y: center.y,
                z: center.z
              },
              width: finalWidth,
              height: finalHeight,
              color: '#ff4d4f', // Default red
              createdAt: new Date().toISOString()
            };
          } else {
            // Line: startPoint to point
            newAnnotation = {
              id: `shape-annotation-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              type: 'ShapeLine',
              shape: 'line',
              geometry: [
                { x: startPoint.x, y: startPoint.y, z: startPoint.z },
                { x: point.x, y: point.y, z: point.z }
              ],
              position: {
                x: (startPoint.x + point.x) / 2,
                y: (startPoint.y + point.y) / 2,
                z: (startPoint.z + point.z) / 2
              },
              color: '#ff4d4f', // Default red
              createdAt: new Date().toISOString()
            };
          }

          // 🆕 Store as pending annotation (NOT added to arrays yet)
          // Only add to arrays when user clicks Save in popup
          setPendingAnnotation(newAnnotation);
          pendingAnnotationIdRef.current = newAnnotation.id;

          // Trigger text annotation popup
          // Use triggerTextAnnotationPopup if available (from DesignerCanvas)
          // Position popup to the right of where user stopped drawing (end point)
          if (triggerTextAnnotationPopup) {
            // Calculate popup position to the right of end point, ensuring it stays within canvas bounds
            const popupPosition = calculatePopupPosition(point);

            // 🆕 Create cancel callback to immediately remove pending annotation
            const cancelCallback = () => {
              console.log('🗑️ CanvasShapeAnnotations - Cancel clicked, removing pending annotation:', newAnnotation.id);
              if (pendingAnnotationIdRef.current === newAnnotation.id) {
                setPendingAnnotation(null);
                pendingAnnotationIdRef.current = null;
              }
            };

            triggerTextAnnotationPopup({
              position: {
                x: popupPosition.x,
                y: popupPosition.y,
                z: popupPosition.z
              },
              annotationData: {
                ...newAnnotation,
                type: newAnnotation.type
              },
              onCancel: cancelCallback, // 🆕 Pass cancel callback
              hideTaskCheckbox: !!shapePopupHideTaskCheckbox
            });
          }

          // Stay in shape-draw so user can draw another shape until toolbar deselect / another tool
        }

        // Reset drawing state
        setIsDrawing(false);
        setStartPoint(null);
        setCurrentPoint(null);
      }
    },
    [
      enabled,
      viewState,
      mode,
      isDrawing,
      startPoint,
      get3dPosition,
      allAnnotations,
      setAllAnnotations,
      triggerTextAnnotationPopup,
      currentShapeType,
      calculatePopupPosition,
      shapePopupHideTaskCheckbox
    ]
  );

  // Handle mouse move
  const handleMouseMove = useCallback(
    (e) => {
      if (!enabled || viewState !== 'annotations' || mode !== 'shape-draw') return;
      if (!isDrawing) return;

      const [point] = get3dPosition(e);
      if (point) {
        setCurrentPoint(point.clone());
      }
    },
    [enabled, viewState, mode, isDrawing, get3dPosition]
  );

  // Set up mousedown event listener (separate useEffect like Drawing component)
  useEffect(() => {
    const canvasElement = canvas?.current || canvas;
    if (!canvasElement) return;

    if (enabled && viewState === 'annotations' && mode === 'shape-draw') {
      canvasElement.addEventListener('mousedown', handleMouseDown, false);
    }

    return () => {
      if (canvasElement) {
        canvasElement.removeEventListener('mousedown', handleMouseDown, false);
      }
    };
  }, [canvas, enabled, viewState, mode, handleMouseDown]);

  // Set up mousemove event listener (separate useEffect like Drawing component)
  useEffect(() => {
    const canvasElement = canvas?.current || canvas;
    if (!canvasElement) return;

    if (enabled && viewState === 'annotations' && mode === 'shape-draw') {
      canvasElement.addEventListener('mousemove', handleMouseMove, false);
    }

    return () => {
      if (canvasElement) {
        canvasElement.removeEventListener('mousemove', handleMouseMove, false);
      }
    };
  }, [canvas, enabled, viewState, mode, handleMouseMove]);

  // Cancel shape drawing on Escape when in shape-draw mode and mid-draw
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (!enabled || viewState !== 'annotations' || mode !== 'shape-draw' || !isDrawing) return;
      e.preventDefault();
      setIsDrawing(false);
      setStartPoint(null);
      setCurrentPoint(null);
    };
    document.addEventListener('keydown', handleKeyDown, false);
    return () => document.removeEventListener('keydown', handleKeyDown, false);
  }, [enabled, viewState, mode, isDrawing]);

  // Sync shape annotations from allAnnotations
  useEffect(() => {
    if (allAnnotations) {
      const shapes = allAnnotations.filter(
        (ann) =>
          ann.type === 'ShapeLine' ||
          ann.shape === 'line' ||
          ann.type === 'ShapeCircle' ||
          ann.shape === 'circle' ||
          ann.type === 'ShapeRectangle' ||
          ann.shape === 'rectangle' ||
          ann.type === 'ShapeX' ||
          ann.shape === 'x'
      );
      setShapeAnnotations(shapes);

      // 🆕 If pending annotation was saved (now in allAnnotations), remove from pending
      if (pendingAnnotation && pendingAnnotationIdRef.current === pendingAnnotation.id) {
        const wasSaved = allAnnotations.some((ann) => ann.id === pendingAnnotation.id);
        if (wasSaved) {
          console.log('✅ CanvasShapeAnnotations - Pending annotation was saved:', pendingAnnotation.id);
          setPendingAnnotation(null);
          pendingAnnotationIdRef.current = null;
        }
      }
    }
  }, [allAnnotations, pendingAnnotation]);

  // Calculate radius for circle preview
  const previewRadius = useMemo(() => {
    if (!isDrawing || !startPoint || !currentPoint) return 0;
    const shapeType = currentShapeType || 'line';
    if (shapeType === 'circle') {
      return startPoint.distanceTo(currentPoint);
    }
    return 0;
  }, [isDrawing, startPoint, currentPoint, currentShapeType]);

  // Calculate preview width and height for rectangle
  const previewDimensions = useMemo(() => {
    if (!isDrawing || !startPoint || !currentPoint) return { width: 0, height: 0 };
    const shapeType = currentShapeType || 'line';
    if (shapeType === 'rectangle') {
      const width = Math.abs(currentPoint.x - startPoint.x);
      const height = Math.abs(currentPoint.z - startPoint.z);
      return { width: Math.max(width, 0.1), height: Math.max(height, 0.1) };
    }
    return { width: 0, height: 0 };
  }, [isDrawing, startPoint, currentPoint, currentShapeType]);

  // Only show shape annotations when user is in annotations feature (match point annotations visibility)
  const inAnnotationsFeature = accordion === 'annotations' || viewState === 'annotations';

  return (
    <group>
      {inAnnotationsFeature && (
        <>
          {/* 🆕 Render pending annotation (temporary, not saved yet) */}
          {pendingAnnotation &&
            (() => {
              const annotation = pendingAnnotation;

              // Render line
              if ((annotation.shape === 'line' || annotation.type === 'ShapeLine') && annotation.geometry?.length === 2) {
                const points = annotation.geometry.map((p) => new Vector3(p.x, p.y, p.z));

                return (
                  <ShapeStroke
                    key={`pending-${annotation.id}`}
                    points={points}
                    color={annotation.color || '#ff4d4f'}
                  />
                );
              }

              // Render circle
              if ((annotation.shape === 'circle' || annotation.type === 'ShapeCircle') && annotation.radius) {
                const center = new Vector3(annotation.position.x, annotation.position.y, annotation.position.z);
                const radius = annotation.radius;

                return (
                  <mesh key={`pending-${annotation.id}`} renderOrder={2} position={center} rotation={[-Math.PI / 2, 0, 0]}>
                    <ringBufferGeometry args={[radius, radius + 0.5, 32]} />
                    <meshBasicMaterial
                      attach="material"
                      color={annotation.color || '#ff4d4f'}
                      depthTest={false}
                      side={THREE.DoubleSide}
                      transparent
                      opacity={1.0}
                    />
                  </mesh>
                );
              }

              // Render rectangle
              if (
                (annotation.shape === 'rectangle' || annotation.type === 'ShapeRectangle') &&
                annotation.width &&
                annotation.height
              ) {
                const center = new Vector3(annotation.position.x, annotation.position.y, annotation.position.z);
                const width = annotation.width;
                const height = annotation.height;

                return (
                  <mesh key={`pending-${annotation.id}`} renderOrder={2} position={center} rotation={[-Math.PI / 2, 0, 0]}>
                    <planeBufferGeometry args={[width, height]} />
                    <meshBasicMaterial
                      attach="material"
                      color={annotation.color || '#ff4d4f'}
                      depthTest={false}
                      side={THREE.DoubleSide}
                      transparent
                      opacity={0.3}
                      wireframe={false}
                    />
                    <lineSegments renderOrder={3}>
                      <edgesGeometry attach="geometry" args={[new THREE.PlaneGeometry(width, height)]} />
                      <lineBasicMaterial attach="material" color={annotation.color || '#ff4d4f'} linewidth={2} />
                    </lineSegments>
                  </mesh>
                );
              }

              // Pending toolbar X: billboard icon until modal confirms save
              if (
                (annotation.shape === 'x' || annotation.type === 'ShapeX') &&
                annotation.markerImage &&
                annotation.position
              ) {
                return (
                  <Html
                    key={`pending-${annotation.id}`}
                    position={[annotation.position.x, annotation.position.y, annotation.position.z]}
                    style={{ pointerEvents: 'none' }}
                  >
                    <div
                      style={{
                        position: 'relative',
                        top: '-30px',
                        pointerEvents: 'none'
                      }}
                    >
                      <img
                        src={annotation.markerImage}
                        alt=""
                        width={SHAPE_X_MARKER_IMG_PX}
                        height={SHAPE_X_MARKER_IMG_PX}
                        style={{
                          position: 'relative',
                          left: '-16px',
                          top: '28px',
                          objectFit: 'contain',
                          pointerEvents: 'none'
                        }}
                      />
                    </div>
                  </Html>
                );
              }

              // Render X shape (legacy mesh only — marker-style X uses markerImage + Html)
              if (
                (annotation.shape === 'x' || annotation.type === 'ShapeX') &&
                annotation.radius &&
                !annotation.markerImage
              ) {
                const center = new Vector3(annotation.position.x, annotation.position.y, annotation.position.z);
                const radius = annotation.radius;

                const halfSize = radius;
                const topLeft = new Vector3(center.x - halfSize, center.y, center.z - halfSize);
                const bottomRight = new Vector3(center.x + halfSize, center.y, center.z + halfSize);
                const topRight = new Vector3(center.x + halfSize, center.y, center.z - halfSize);
                const bottomLeft = new Vector3(center.x - halfSize, center.y, center.z + halfSize);

                const line1Points = [topLeft, bottomRight];
                const line2Points = [topRight, bottomLeft];

                return (
                  <group key={`pending-${annotation.id}`}>
                    <ShapeStroke points={line1Points} color={annotation.color || '#ff4d4f'} />
                    <ShapeStroke points={line2Points} color={annotation.color || '#ff4d4f'} />
                  </group>
                );
              }

              return null;
            })()}

          {/* Render existing shape annotations */}
          {shapeAnnotations.map((annotation) => {
            // Render line
            if ((annotation.shape === 'line' || annotation.type === 'ShapeLine') && annotation.geometry?.length === 2) {
              const points = annotation.geometry.map((p) => new Vector3(p.x, p.y, p.z));

              return (
                <ShapeStroke
                  key={annotation.id}
                  points={points}
                  color={annotation.color || '#ff4d4f'}
                />
              );
            }

            // Render circle
            if ((annotation.shape === 'circle' || annotation.type === 'ShapeCircle') && annotation.radius) {
              const center = new Vector3(annotation.position.x, annotation.position.y, annotation.position.z);
              const radius = annotation.radius;

              return (
                <mesh key={annotation.id} renderOrder={2} position={center} rotation={[-Math.PI / 2, 0, 0]}>
                  <ringBufferGeometry args={[radius, radius + 0.5, 32]} />
                  <meshBasicMaterial
                    attach="material"
                    color={annotation.color || '#ff4d4f'}
                    depthTest={false}
                    side={THREE.DoubleSide}
                    transparent
                    opacity={1.0}
                  />
                </mesh>
              );
            }

            // Render rectangle
            if (
              (annotation.shape === 'rectangle' || annotation.type === 'ShapeRectangle') &&
              annotation.width &&
              annotation.height
            ) {
              const center = new Vector3(annotation.position.x, annotation.position.y, annotation.position.z);
              const width = annotation.width;
              const height = annotation.height;

              return (
                <mesh key={annotation.id} renderOrder={2} position={center} rotation={[-Math.PI / 2, 0, 0]}>
                  <planeBufferGeometry args={[width, height]} />
                  <meshBasicMaterial
                    attach="material"
                    color={annotation.color || '#ff4d4f'}
                    depthTest={false}
                    side={THREE.DoubleSide}
                    transparent
                    opacity={0.3}
                    wireframe={false}
                  />
                  {/* Wireframe outline */}
                  <lineSegments renderOrder={3}>
                    <edgesGeometry attach="geometry" args={[new THREE.PlaneGeometry(width, height)]} />
                    <lineBasicMaterial attach="material" color={annotation.color || '#ff4d4f'} linewidth={2} />
                  </lineSegments>
                </mesh>
              );
            }

            // Render X shape (legacy mesh only)
            if ((annotation.shape === 'x' || annotation.type === 'ShapeX') && annotation.radius && !annotation.markerImage) {
              const center = new Vector3(annotation.position.x, annotation.position.y, annotation.position.z);
              const radius = annotation.radius;

              // Calculate the four corner points for the X
              // X is two diagonal lines crossing at center
              // Line 1: top-left to bottom-right
              // Line 2: top-right to bottom-left
              const halfSize = radius;
              const topLeft = new Vector3(center.x - halfSize, center.y, center.z - halfSize);
              const bottomRight = new Vector3(center.x + halfSize, center.y, center.z + halfSize);
              const topRight = new Vector3(center.x + halfSize, center.y, center.z - halfSize);
              const bottomLeft = new Vector3(center.x - halfSize, center.y, center.z + halfSize);

              // Create two lines for the X
              const line1Points = [topLeft, bottomRight];
              const line2Points = [topRight, bottomLeft];

              return (
                <group key={annotation.id}>
                  <ShapeStroke points={line1Points} color={annotation.color || '#ff4d4f'} />
                  <ShapeStroke points={line2Points} color={annotation.color || '#ff4d4f'} />
                </group>
              );
            }

            return null;
          })}

          {/* Render preview while drawing */}
          {isDrawing && startPoint && currentPoint && (
            <>
              {currentShapeType === 'circle' ? (
                // Preview circle
                <mesh renderOrder={2} position={startPoint} rotation={[-Math.PI / 2, 0, 0]}>
                  <ringBufferGeometry args={[previewRadius, previewRadius + 0.5, 32]} />
                  <meshBasicMaterial
                    attach="material"
                    color="#ff4d4f"
                    depthTest={false}
                    side={THREE.DoubleSide}
                    transparent
                    opacity={0.8}
                  />
                </mesh>
              ) : currentShapeType === 'rectangle' ? (
                // Preview rectangle
                <mesh
                  renderOrder={2}
                  position={
                    new Vector3(
                      (startPoint.x + currentPoint.x) / 2,
                      (startPoint.y + currentPoint.y) / 2,
                      (startPoint.z + currentPoint.z) / 2
                    )
                  }
                  rotation={[-Math.PI / 2, 0, 0]}
                >
                  <planeBufferGeometry args={[previewDimensions.width, previewDimensions.height]} />
                  <meshBasicMaterial
                    attach="material"
                    color="#ff4d4f"
                    depthTest={false}
                    side={THREE.DoubleSide}
                    transparent
                    opacity={0.3}
                  />
                  {/* Wireframe outline */}
                  <lineSegments renderOrder={3}>
                    <edgesGeometry
                      attach="geometry"
                      args={[new THREE.PlaneGeometry(previewDimensions.width, previewDimensions.height)]}
                    />
                    <lineBasicMaterial attach="material" color="#ff4d4f" linewidth={2} />
                  </lineSegments>
                </mesh>
              ) : (
                // Preview line
                <DreiLine
                  points={[startPoint, currentPoint]}
                  color="#ff4d4f"
                  lineWidth={2}
                  transparent
                  opacity={0.8}
                  depthTest={false}
                  renderOrder={2}
                />
              )}
            </>
          )}
        </>
      )}
    </group>
  );
};

export default CanvasShapeAnnotations;
